import { useCallback, useMemo } from "react";
import clsx from "clsx";
import {
  Box,
  Grid,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FormikProps, useFormikContext } from "formik";

import Alert from "components/shared/Alert";
import FormikFormField from "components/shared/FormikFormField";
import Link from "components/shared/Link";
import useApp from "hooks/useApp";
import useCatalogDetail from "hooks/useCatalogDetail";
import { colors } from "theme";
import { DesignType } from "types/design";
import { IDiseaseType } from "types/diseaseType";
import { ICustomPanelForm } from "types/form";

const useStyles = makeStyles((theme) => ({
  container: {
    background: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  containerCustom: {
    padding: theme.spacing(2),
    [theme.breakpoints.down("sm")]: { paddingBottom: 0 },
  },
  containerDefault: { paddingTop: theme.spacing(2) },
  title: {
    [theme.breakpoints.down("sm")]: {
      fontWeight: 600,
      fontSize: 21,
      lineHeight: "auto",
    },
  },
  detailLabel: {
    pointerEvents: "none",
    "&.MuiInputLabel-filled.MuiInputLabel-shrink": {
      color: colors.nero[300],
      fontSize: theme.typography.h5.fontSize,
      fontWeight: theme.typography.h5.fontWeight,
    },
  },
  detailLabelCustom: { lineHeight: "24px" },
  detailTextField: {
    "&.MuiTextField-root > .MuiFilledInput-root > .MuiFilledInput-input": {
      fontSize: theme.typography.body1.fontSize,
    },
    "& > .MuiInputBase-root": { margin: theme.spacing(2, 0) },
    "& > .MuiFilledInput-multiline.MuiFilledInput-marginDense": {
      paddingTop: 18,
    },
  },
  detailTextFieldCustom: {
    [theme.breakpoints.down("sm")]: { marginBottom: theme.spacing(2) },
    "&.MuiTextField-root > .MuiFilledInput-root > .MuiFilledInput-input": {
      fontSize: theme.typography.body1.fontSize,
    },
    "& > .MuiInputBase-root": { marginTop: theme.spacing(1.5) },
  },
  workflowType: {
    [theme.breakpoints.down("sm")]: { marginLeft: theme.spacing(-2) },
  },
  diseaseTypes: {
    padding: "0 0 0 4px !important",
  },
}));

const PanelDetailHeader = () => {
  const classes = useStyles();
  const { getWorkflow, getDiseaseTypes } = useApp();
  const {
    catalog,
    customize,
    diseaseType,
    draftAlertStatus,
    isCustomPanel,
    isDraftCustomPanel,
    setDiseaseType,
    setDraftAlertStatus,
  } = useCatalogDetail();
  const formProps: FormikProps<ICustomPanelForm> = useFormikContext();

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  const getTitle = useCallback(() => {
    if (isDraftCustomPanel) {
      return customize ? "Customize Panel" : "Draft Custom Panel Detail";
    }
    if (isCustomPanel) {
      return customize ? "Customize Panel" : "Custom Panel Detail";
    }
    if (catalog?.designType === DesignType.CATALOG_PANEL) {
      return customize ? "Customize Catalog Panel" : "Catalog Panel Detail";
    }
    return customize ? "Customize Gene Module" : "Gene Module Detail";
  }, [customize, isCustomPanel, catalog?.designType, isDraftCustomPanel]);

  const valueOfDiseaseType = useMemo(() => {
    if (customize && !!diseaseType) {
      return diseaseType?.name || "";
    } else {
      const diseaseTypes = getDiseaseTypes(catalog?.diseaseType);
      setTimeout(() => setDiseaseType(diseaseTypes[0]), 0);
      return diseaseTypes
        .map((diseaseType: IDiseaseType) => diseaseType.name)
        .join("\n");
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [diseaseType, catalog?.diseaseType, customize]);

  return (
    <Box display="flex" flexDirection="column" rowGap={isMobile ? 2 : 3}>
      <Typography variant="h1" className={classes.title}>
        {getTitle()}
      </Typography>
      {!!draftAlertStatus && (
        <Alert
          severity={draftAlertStatus === "success" ? "info" : "warning"}
          icon={false}
        >
          {draftAlertStatus === "success" && (
            <Box>
              <Typography variant="inherit" component="span">
                The draft panel has been saved and can be viewed in{" "}
                <Link
                  url="/dashboard/draft-custom-panels"
                  color="secondary"
                  underline="always"
                >
                  Draft Custom Panels
                </Link>
                .
              </Typography>
            </Box>
          )}
          {draftAlertStatus === "error" &&
            "An error occurred. Please try again or contact support."}
        </Alert>
      )}
      <Grid
        container
        className={clsx(
          classes.container,
          customize ? classes.containerCustom : classes.containerDefault
        )}
      >
        <Grid item xs={12} sm={6}>
          {customize ? (
            <Typography variant="h5" className={classes.detailLabelCustom}>
              Name
            </Typography>
          ) : null}
          <FormikFormField<ICustomPanelForm>
            name="panelName"
            required={false}
            label={customize ? "Custom Panel Name" : "Name"}
            textFieldProps={{
              classes: customize
                ? { root: classes.detailTextFieldCustom }
                : { root: classes.detailTextField },
              InputLabelProps: customize
                ? { shrink: false }
                : { className: classes.detailLabel },
              InputProps: { readOnly: !customize },
              helperText: customize
                ? "Name required (max 30 characters and alphanumerical)"
                : "",
              onKeyPress: (event: React.KeyboardEvent) => {
                event.key === "Enter" && event.preventDefault();
                // Close any message on that page
                if (draftAlertStatus) {
                  setDraftAlertStatus(null);
                }
              },
            }}
            {...formProps}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Workflow"
            value={getWorkflow(catalog?.workflow)?.name || ""}
            fullWidth
            size="small"
            variant="filled"
            classes={{
              root: clsx(
                classes.detailTextField,
                customize && classes.workflowType
              ),
            }}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ className: classes.detailLabel }}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Disease Type"
            multiline
            value={valueOfDiseaseType}
            fullWidth
            size="small"
            variant="filled"
            classes={{ root: classes.detailTextField }}
            inputProps={{ className: classes.diseaseTypes }}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ className: classes.detailLabel }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PanelDetailHeader;
